import React, {useCallback, useEffect, useState} from "react";
import {ReactFlowProvider} from "@xyflow/react";
import {type BranchPythonOperator, type FanOutOperator, type LLMOperator, Operator, OperatorTypeEnum} from "@/model/Workflow";
import LayoutFlow, {
    type AddEdgeBranchPayload,
    type UpdateBranchEdgePayload
} from "@/view/workflow/components/workflowGraph/LayoutFlow";
import {buildOperatorEdgeId, getOperatorNodeType} from "@/view/workflow/helper/baseOperatorHelper";
import type {OperatorNodeData, OperatorEdge, OperatorNode,} from "@/view/workflow/components/workflowGraph/types";

const GRAPH_STATUS_COLORS = {
    visited: '#3b82f6',
    failed: '#ef4444',
    focused: '#a855f7'
}

interface WorkflowGraphProps {
    operators: {[key: string]: Operator};
    editable: boolean;
    focusedNodeId?: string;
    visitedNodeIds?: string[];
    failedNodeIds?: string[];
    operatorDurations?: {[key: string]: number};
    operatorExecutionOrders?: {[key: string]: number[]};
    onClickNode?: (node: OperatorNode) => void;
    onAddOperator?: (node: OperatorNode) => void;
    onDeleteOperator?: (operatorId: string) => void;
    onAddEdge?: (source: string, target: string, branch?: AddEdgeBranchPayload) => void;
    onUpdateBranchEdge?: (source: string, target: string, payload: UpdateBranchEdgePayload) => void;
    onDeleteEdge?: (source: string, target: string, branch?: AddEdgeBranchPayload) => void;
    toolbarActions?: React.ReactNode;
}

const WorkflowGraph: React.FC<WorkflowGraphProps> = (props) => {
    const {
        operators,
        editable,
        focusedNodeId,
        visitedNodeIds,
        failedNodeIds,
        operatorDurations,
        operatorExecutionOrders,
        onClickNode,
        onDeleteOperator,
        onAddOperator,
        onAddEdge,
        onUpdateBranchEdge,
        onDeleteEdge,
        toolbarActions
    } = props;
    const [graphData, setGraphData] = useState<{nodes: OperatorNode[], edges: OperatorEdge[]} | undefined>(undefined);

    const convertOperatorToNode = useCallback((operator: Operator): OperatorNode => {
        let borderColor: string | undefined;
        if (focusedNodeId === operator._id)
            borderColor = GRAPH_STATUS_COLORS.focused;
        else if (failedNodeIds?.includes(operator._id))
            borderColor = GRAPH_STATUS_COLORS.failed;
        else if (visitedNodeIds?.includes(operator._id))
            borderColor = GRAPH_STATUS_COLORS.visited;

        const nodeData: OperatorNodeData = {
            name: operator.name || operator._id,
            type: operator.type,
            editable,
            borderColor,
            borderWidth: 2,
            duration:
                operatorDurations && operator._id in operatorDurations
                    ? operatorDurations[operator._id]
                    : undefined,
            executionOrders: operatorExecutionOrders?.[operator._id],
            recursionLimit: operator.recursionLimit
        };
        const position = {x: 0, y: 0};
        const nodeType = getOperatorNodeType(operator.type);
        switch (nodeType) {
            case "generalNode": {
                return {
                    id: operator._id,
                    type: nodeType,
                    position,
                    data: nodeData,
                };
            }
            case "branchNode": {
                const branchOperator = operator as BranchPythonOperator;
                return {
                    id: operator._id,
                    type: nodeType,
                    position,
                    data: {
                        ...nodeData,
                        branchMap: branchOperator.branchMap,
                    },
                };
            }
            case "llmNode": {
                const llmOperator = operator as LLMOperator;
                return {
                    id: operator._id,
                    type: nodeType,
                    position,
                    data: {
                        ...nodeData,
                        model: llmOperator.model,
                    },
                };
            }
        }
    }, [editable, failedNodeIds, visitedNodeIds, focusedNodeId, operatorDurations, operatorExecutionOrders])

    const convertOperatorToEdges = useCallback((
        operator: Operator
    ): OperatorEdge[] => {
        const edges: OperatorEdge[] = [];

        if (operator.type === OperatorTypeEnum.BRANCH_PYTHON) {
            Object.entries(operator.branchMap).forEach((entry) => {
                const branchName = entry[0]
                const branchedOperatorId = entry[1]
                edges.push({
                    id: buildOperatorEdgeId(operator._id, branchedOperatorId, branchName),
                    source: operator._id,
                    target: branchedOperatorId,
                    type: 'default',
                    label: branchName,
                    data: {branchKey: branchName},
                    style: (visitedNodeIds?.includes(branchedOperatorId)) ? {
                        strokeWidth: 2,
                        stroke: GRAPH_STATUS_COLORS.visited
                    } : undefined
                })
            })
        } else if (operator.type === OperatorTypeEnum.FAN_OUT) {
            const fanOutOperator = operator as FanOutOperator;
            fanOutOperator.nextOperatorIds.forEach((nextOperatorId) => {
                edges.push({
                    id: buildOperatorEdgeId(operator._id, nextOperatorId),
                    source: operator._id,
                    target: nextOperatorId,
                    type: 'default',
                    style: (visitedNodeIds?.includes(nextOperatorId)) ? {
                        strokeWidth: 2,
                        stroke: GRAPH_STATUS_COLORS.visited
                    } : undefined
                })
            })
        } else if (operator.nextOperatorId) {
            edges.push({
                id: buildOperatorEdgeId(operator._id, operator.nextOperatorId),
                source: operator._id,
                target: operator.nextOperatorId,
                type: 'default',
                style: (visitedNodeIds?.includes(operator.nextOperatorId)) ? {
                    strokeWidth: 2,
                    stroke: GRAPH_STATUS_COLORS.visited
                } : undefined

            })
        }
        return edges;
    }, [visitedNodeIds])

    useEffect(() => {
        const nodes: OperatorNode[] = [];
        const edges: OperatorEdge[] = [];
        Object.values(operators).forEach((operator) => {
            nodes.push(convertOperatorToNode(operator));
            edges.push(...convertOperatorToEdges(operator));
        })
        setGraphData({nodes, edges});
    }, [operators, convertOperatorToNode, convertOperatorToEdges])

    if (!graphData)
        return null;

    return (
        <div className="w-full h-full grow overflow-hidden">
            <ReactFlowProvider>
                <LayoutFlow nodes={graphData.nodes}
                            edges={graphData.edges}
                            editable={editable}
                            onClickNode={onClickNode}
                            onAddOperator={onAddOperator}
                            onDeleteOperator={onDeleteOperator}
                            onAddEdge={onAddEdge}
                            onUpdateBranchEdge={onUpdateBranchEdge}
                            onDeleteEdge={onDeleteEdge}
                            toolbarActions={toolbarActions}
                />
            </ReactFlowProvider>
        </div>
    )
}

export default WorkflowGraph
